import React, { useMemo } from 'react';
import { PencilIcon, PlusIcon } from 'lucide-react';
import type { Milestone } from '../types/life';
import { cn } from '../utils/cn';
import { formatCountdown, formatElapsed } from '../utils/format';
import { milestoneStart, milestoneUnitLabel } from '../utils/lifeGrid';
import { useNow } from '../hooks/useNow';
interface MilestonePanelProps {
  milestones: Milestone[];
  onAdd: () => void;
  onEdit: (milestone: Milestone) => void;
  onFocus: (milestone: Milestone) => void;
}
interface RowProps {
  milestone: Milestone;
  now: Date;
  ahead: boolean;
  onEdit: (milestone: Milestone) => void;
  onFocus: (milestone: Milestone) => void;
}
function MilestoneRow({
  milestone,
  now,
  ahead,
  onEdit,
  onFocus
}: RowProps) {
  const start = milestoneStart(milestone);
  return <li className="group flex items-baseline gap-3 py-1.5" data-id="element-37" data-mp="3r0gdmee9tx9g">
      <button type="button" onClick={() => onFocus(milestone)} className="min-w-0 flex-1 text-left" aria-label={`Показать «${milestone.title}» в сетке`} data-id="element-38" data-mp="3r214nu1dmy2k">
        <span className={cn('block truncate text-sm font-medium transition-colors duration-150 ease-swift', ahead ? 'text-ink group-hover:text-accent' : 'text-muted line-through decoration-1')} data-id="element-39" data-mp="3glc6rziz1bio">
          {milestone.title}
        </span>
        <span className="text-muted mt-0.5 block truncate font-mono text-[11px]" data-id="element-40" data-mp="3bkev85sgvfq5">
          {milestoneUnitLabel(milestone)}
        </span>
      </button>
      <span className={cn('shrink-0 font-mono text-xs tabular-nums', ahead ? 'text-accent' : 'text-muted')} aria-live={ahead ? 'off' : undefined} data-id="element-41" data-mp="32krpx8wm9prt">
        {ahead ? formatCountdown(start, now) : formatElapsed(start, now)}
      </span>
      <button type="button" onClick={() => onEdit(milestone)} aria-label={`Изменить «${milestone.title}»`} className="text-muted hover:text-ink shrink-0 opacity-0 transition-opacity duration-150 ease-swift focus-visible:opacity-100 group-hover:opacity-100" data-id="element-42" data-mp="2svhx1qty832n">
        <PencilIcon className="h-3.5 w-3.5" data-id="element-43" data-mp="pfmo8oxv7afa" />
      </button>
    </li>;
}
export function MilestonePanel({
  milestones,
  onAdd,
  onEdit,
  onFocus
}: MilestonePanelProps) {
  const now = useNow(1000);
  const {
    ahead,
    behind
  } = useMemo(() => {
    const withStart = milestones.map(m => ({
      m,
      start: milestoneStart(m)
    }));
    return {
      ahead: withStart.filter(x => x.start.getTime() > now.getTime()).sort((a, b) => a.start.getTime() - b.start.getTime()).map(x => x.m),
      behind: withStart.filter(x => x.start.getTime() <= now.getTime()).sort((a, b) => b.start.getTime() - a.start.getTime()).map(x => x.m)
    };
    // now меняется каждую секунду, но пересортировка дешёвая
  }, [milestones, now]);
  return <section className="border-line bg-paper shrink-0 border-t px-4 py-3 sm:px-6 sm:py-4" aria-label="Майлстоуны" data-id="element-44" data-mp="3j60gpvk1uukc">
      <div className="flex items-center justify-between gap-4" data-id="element-45" data-mp="3f5qdgqnn6i96">
        <h2 className="text-ink text-sm font-semibold tracking-tight" data-id="element-46" data-mp="1umwafbsxrtqt">Майлстоуны</h2>
        <button type="button" onClick={onAdd} className="text-muted hover:text-ink flex items-center gap-1.5 text-xs font-medium transition-colors duration-150 ease-swift" data-id="element-47" data-mp="1ymexad4biewa">
          <PlusIcon className="h-3.5 w-3.5" data-id="element-48" data-mp="28drn6z6k2uzo" />
          Добавить
        </button>
      </div>

      <div className="mt-3 grid gap-x-8 gap-y-4 md:grid-cols-2" data-id="element-49" data-mp="2db1aa86h2ukc">
        <div data-id="element-50" data-mp="2iaxdw6b9cjzd">
          <p className="text-muted mb-1 text-[11px]" data-id="element-51" data-mp="2ianvp4wgfh48">Впереди</p>
          {ahead.length === 0 ? <p className="text-muted py-1.5 text-xs" data-id="element-52" data-mp="2mc93ibu4t57k">
              Ничего не ждёт. Кликни по любой клетке в сетке, чтобы поставить точку впереди.
            </p> : <ul className="divide-line thin-scroll max-h-[76px] divide-y overflow-y-auto pr-1 md:max-h-[124px]" data-id="element-53" data-mp="ryriowl42mg8">
              {ahead.map(m => <MilestoneRow key={m.id} milestone={m} now={now} ahead onEdit={onEdit} onFocus={onFocus} data-id="element-54" data-mp="wym9z18ste1t" />)}
            </ul>}
        </div>

        <div className="border-line md:border-l md:pl-8" data-id="element-55" data-mp="3suh2j3n4u9ki">
          <p className="text-muted mb-1 text-[11px]" data-id="element-56" data-mp="3sw1ucli2beeo">Позади</p>
          {behind.length === 0 ? <p className="text-muted py-1.5 text-xs" data-id="element-57" data-mp="bjrcfv565jf2">Пока ничего не осталось за спиной.</p> : <ul className="divide-line thin-scroll max-h-[76px] divide-y overflow-y-auto pr-1 md:max-h-[124px]" data-id="element-58" data-mp="6kwlu70ymqke">
              {behind.map(m => <MilestoneRow key={m.id} milestone={m} now={now} ahead={false} onEdit={onEdit} onFocus={onFocus} data-id="element-59" data-mp="3a4iswj3os8er" />)}
            </ul>}
        </div>
      </div>
    </section>;
}